'use client';

import {
  forwardRef,
  type Ref,
  useMemo,
  useEffect,
  useId,
  useState,
  useRef,
} from 'react';
import styles from './Input.module.css';
import { getClasses } from '@/_utils/classnames';
import { InputProps } from './Input.types';
import {
  Button,
  Flex,
  Icon,
  Loading,
  Pill,
  Popover,
  ResultList,
  ResultListItem,
  Text,
} from '@/_components';
import { IconType } from '@/_types/icons';
import { InputMask } from '@react-input/mask';
import { LIMIT_ENTITY_SELECTOR_CHIPS } from '@/_constants/limits';

const DEFAULT_INPUT_HEIGHT = 44 as const;

const Input = forwardRef<HTMLInputElement, InputProps>(
  (props: InputProps, ref: Ref<HTMLInputElement>) => {
    const {
      autoComplete = false,
      autoFocus,
      chipExtraCount,
      chipNames,
      chips,
      className,
      clearChipsText,
      clearable: isClearable,
      hasNoChipLimit,
      helperText,
      helperTextColor,
      iconHelperText,
      iconLeft,
      iconLeftColor,
      iconRight,
      iconRightColor,
      id: incomingId,
      isActive: isActiveFromProps,
      isDisabled,
      isLabelHidden,
      isLoading,
      isRequired,
      label,
      manualDropDownWidth,
      mask,
      max,
      maxLength,
      min,
      minLength,
      multipleSelect,
      name,
      onBlur: onBlurFromProps,
      onChange,
      onClearChips,
      onFocus: onFocusFromProps,
      onKeyPress,
      onLeftClick,
      onRemoveChip,
      onResultSelect,
      onRightClick,
      pattern,
      persistClearButton,
      placeholder,
      results = [],
      role = 'textbox',
      type: incomingType,
      value,
      'data-testid': dataTestId,
    } = props;
    const pillContainer = useRef<HTMLDivElement | null>(null);
    const genId: string = useId();
    const id: string = incomingId || genId;

    const [isActive, setActive] = useState<boolean>(isActiveFromProps || false);
    const [inputHeight, setInputHeight] =
      useState<number>(DEFAULT_INPUT_HEIGHT);
    const [type, setType] = useState<InputProps['type']>(incomingType);

    useEffect(() => {
      setActive(false);
    }, [isActiveFromProps]);

    const inputClasses: string = getClasses(styles.input, {
      [styles.inputActive]: isActive,
      [styles.inputDisabled]: isDisabled,
      [styles.inputWithIconLeft]: !!iconLeft,
      [styles.inputWithIconRight]: !!iconRight,
      [styles.inputDate]: type === 'date',
      [styles.inputError]: props.isError,
    });

    const isPasswordType: boolean = Boolean(incomingType === 'password');
    const passwordLeftIcon: IconType =
      type === 'password' ? 'eye-closed' : 'eye';
    const onTogglePassword = (): void =>
      setType(type === 'password' ? 'text' : 'password');

    const onBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onBlurFromProps) onBlurFromProps(e);
      setTimeout(() => {
        if (isActiveFromProps === undefined) setActive(false);
      }, 200);
    };
    const onFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onFocusFromProps) onFocusFromProps(e);
      setActive(true);
    };

    const chipsArray: Array<string> = useMemo(
      () => (chips ? [...chips] : []),
      [chips],
    );

    const isChipsActive: boolean = useMemo(
      () => Boolean(inputHeight && chipsArray?.length),
      [inputHeight, chipsArray?.length],
    );

    const visibleChips: Array<string> = useMemo(() => {
      if (hasNoChipLimit) return chipsArray;

      if (chipsArray.length <= LIMIT_ENTITY_SELECTOR_CHIPS) {
        return chipsArray;
      }

      const remainingChipCount =
        chipsArray.length - LIMIT_ENTITY_SELECTOR_CHIPS;

      return [
        ...chipsArray.slice(0, LIMIT_ENTITY_SELECTOR_CHIPS),
        `+${remainingChipCount} more`,
      ];
    }, [chipsArray, hasNoChipLimit]);

    const resultsAndChips: Array<ResultListItem> = useMemo(
      () =>
        results?.map((r) => {
          const isChecked: boolean =
            chipsArray.includes(r.name!) || chipsArray.includes(r.value!);

          if (isChecked) r.icon = 'check-circle-filled';
          else r.icon = 'circle';

          return r;
        }) || [],
      [results, chipsArray],
    );

    useEffect(() => {
      const pillContainerRect = pillContainer?.current?.getBoundingClientRect();
      const newHeight: number = (pillContainerRect?.height ?? 0) + 4;

      pillContainerRect?.height && setInputHeight(newHeight);
    }, [chipNames, chipsArray, isLabelHidden, chipExtraCount]);

    const showResults: boolean = isChipsActive
      ? Boolean(isActive && resultsAndChips?.length)
      : Boolean(isActive && results?.length);

    const inputPillClasses: string = getClasses(styles.inputPills, {
      [styles.inputPillsNoLabel]: isLabelHidden,
      [styles.inputPillsClearable]: isClearable,
    });

    const inputProps: React.InputHTMLAttributes<HTMLInputElement> & {
      'data-testid'?: string;
    } = {
      autoFocus,
      autoComplete: autoComplete ? autoComplete.toString() : undefined,
      className: inputClasses,
      disabled: !!isDisabled,
      id,
      onKeyPress,
      style:
        isChipsActive || !!chipExtraCount
          ? {
              paddingTop: `${inputHeight}px`,
              height: `calc(${inputHeight}px + ${DEFAULT_INPUT_HEIGHT}px)`,
            }
          : undefined,
      min,
      name,
      maxLength,
      minLength,
      max,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      role,
      type,
      value,
      'data-testid': dataTestId,
    };

    const inputContainerClasses: string = getClasses(
      styles.inputContainer,
      className,
    );

    const shouldShowClearButton: boolean =
      Boolean(isClearable) && (persistClearButton || isChipsActive);

    return (
      <Flex
        isColumn={true}
        gap={!isLabelHidden || shouldShowClearButton ? 2 : 0}
        className={inputContainerClasses}
      >
        {(!isLabelHidden || shouldShowClearButton) && (
          <Flex
            isBetween={!isLabelHidden}
            xAlign={isLabelHidden ? 'end' : undefined}
          >
            {!isLabelHidden && (
              <label htmlFor={id} className="flex gap-2 flex-y-center">
                <Text
                  type="small"
                  fontWeight="medium"
                  color={isDisabled ? 'secondary' : 'primary'}
                >
                  {label}
                </Text>
                {isRequired && (
                  <Text type="small" color="critical">
                    *
                  </Text>
                )}
              </label>
            )}
            {shouldShowClearButton && (
              <Button
                intent="danger-ghost"
                size="tiny"
                value={clearChipsText ?? 'Clear all'}
                onClick={onClearChips}
              />
            )}
          </Flex>
        )}
        <Popover
          inheritTriggerWidth={!manualDropDownWidth}
          open={showResults}
          placement="bottom"
          role="listbox"
        >
          <Popover.Trigger>
            <Flex className={styles.inputContainer}>
              {iconLeft && (
                <Icon
                  name={iconLeft}
                  size={20}
                  onClick={onLeftClick}
                  color={iconLeftColor}
                  className={styles.input__iconLeft}
                />
              )}
              {mask ? (
                <InputMask
                  mask={mask}
                  replacement={{ _: /\d/ }}
                  className={inputClasses}
                  {...inputProps}
                />
              ) : (
                <input
                  ref={ref}
                  className={inputClasses}
                  pattern={pattern}
                  aria-label={label}
                  onKeyDown={(event) => {
                    if (event.key === ' ') {
                      event.stopPropagation();
                    }
                  }}
                  {...inputProps}
                />
              )}
              {isLoading && (
                <Loading size={16} className={styles.input__iconLoading} />
              )}
              {!isLoading && iconRight && (
                <Icon
                  name={isPasswordType ? passwordLeftIcon : iconRight}
                  size={20}
                  color={iconRightColor}
                  onClick={isPasswordType ? onTogglePassword : onRightClick}
                  className={styles.input__iconRight}
                />
              )}
            </Flex>
          </Popover.Trigger>
          <Popover.Content
            isFullWidth={!manualDropDownWidth}
            padding={2}
            isColumn={true}
          >
            <ResultList
              items={isChipsActive ? resultsAndChips : results!}
              onSelect={onResultSelect}
              card={false} // TODO: refactor `ResultList` and `Input` such that this is not necessary
              multipleSelect={multipleSelect}
            />
          </Popover.Content>
        </Popover>
        {(!!chipExtraCount || !!visibleChips?.length) && (
          <div className={inputPillClasses} ref={pillContainer}>
            {visibleChips?.map((chip, key) => {
              const onRemoveChipClick = () => onRemoveChip?.(chip);
              const chipName: string =
                chipNames?.find((r) => r.value === chip)?.name || chip;
              const isMoreIndicator: boolean = chip.startsWith('+');
              const showRemoveIcon: IconType | undefined = !isMoreIndicator
                ? 'x'
                : undefined;

              return (
                <Pill
                  intent={isMoreIndicator ? 'default' : 'secondary'}
                  key={key}
                  label={chipName}
                  iconRight={showRemoveIcon}
                  onRightClick={showRemoveIcon ? onRemoveChipClick : undefined}
                  isEllipsis={true}
                />
              );
            })}
            {!!chipExtraCount && (
              <Pill
                intent="danger"
                className={styles.input__extraPill}
                label={`${chipExtraCount} bulk selected`}
                isEllipsis={true}
              />
            )}
          </div>
        )}
        {!!helperText && (
          <Flex isYCentered={true} gap={2}>
            {iconHelperText && (
              <Icon
                name={iconHelperText}
                size={16}
                className={styles.input__iconHelper}
              />
            )}
            <Text type="tiny" color={helperTextColor ?? 'secondary'}>
              {helperText}
            </Text>
          </Flex>
        )}
      </Flex>
    );
  },
);

Input.displayName = 'Input';

export { Input };
